/**
 * Demo Database Loader - Internal IndexedDB Functions
 *
 * Shared IndexedDB functions used by both demo and upload database loaders.
 */

/** IndexedDB database name for storage */
const IDB_DB_NAME = 'sql-studio-demo-cache';
const IDB_STORE_NAME = 'databases';
const IDB_VERSION = 1;

/**
 * Check if IndexedDB is available.
 */
export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Open IndexedDB for caching.
 */
export async function openIndexedDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_DB_NAME, IDB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get cached data from IndexedDB.
 */
export async function getFromIndexedDb(key: string): Promise<ArrayBuffer | null> {
  try {
    const db = await openIndexedDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readonly');
      const store = tx.objectStore(IDB_STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        db.close();
        resolve(request.result || null);
      };
      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });
  } catch (error) {
    console.warn('[IndexedDB] Get failed:', error);
    return null;
  }
}

/**
 * Save data to IndexedDB.
 */
export async function saveToIndexedDb(key: string, data: ArrayBuffer): Promise<boolean> {
  try {
    const db = await openIndexedDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      const store = tx.objectStore(IDB_STORE_NAME);
      const request = store.put(data, key);

      request.onsuccess = () => {
        db.close();
        resolve(true);
      };
      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });
  } catch (error) {
    console.warn('[IndexedDB] Save failed:', error);
    return false;
  }
}

/**
 * Delete data from IndexedDB.
 */
export async function deleteFromIndexedDb(key: string): Promise<boolean> {
  try {
    const db = await openIndexedDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      const store = tx.objectStore(IDB_STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => {
        db.close();
        resolve(true);
      };
      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });
  } catch (error) {
    console.warn('[IndexedDB] Delete failed:', error);
    return false;
  }
}

/**
 * Clear all data from IndexedDB.
 */
export async function clearIndexedDb(): Promise<void> {
  try {
    const db = await openIndexedDb();
    const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
    const store = tx.objectStore(IDB_STORE_NAME);
    store.clear();

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    db.close();
    console.log('[IndexedDB] Cleared all cached data');
  } catch (error) {
    console.warn('[IndexedDB] Failed to clear:', error);
  }
}
